import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const DriverPricing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';

  // NEW: Get required days from navigation state
  const requiredDays = location.state?.requiredDays || [];
  
  const [availabilityWindows, setAvailabilityWindows] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchAvailabilityAndPricing();
  }, []);

  const fetchAvailabilityAndPricing = async () => {
    try {
      setLoading(true);
      
      // Fetch availability windows
      const availResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/availability/days`);
      
      // Transform availability into pricing windows structure
      // Now handles start_time_1, start_time_2, start_time_3 format
      const windows = [];
      availResponse.data.forEach((avail) => {
        if (avail.is_available) {
          // Check for up to 3 time windows per day
          if (avail.start_time_1 && avail.end_time_1) {
            windows.push({
              id: `${avail.day_of_week}-${avail.start_time_1}-${avail.end_time_1}`,
              day: avail.day_of_week,
              availStart: avail.start_time_1,
              availEnd: avail.end_time_1,
              segments: [] // Will be populated with pricing segments
            });
          }
          if (avail.start_time_2 && avail.end_time_2) {
            windows.push({
              id: `${avail.day_of_week}-${avail.start_time_2}-${avail.end_time_2}`,
              day: avail.day_of_week,
              availStart: avail.start_time_2,
              availEnd: avail.end_time_2,
              segments: []
            });
          }
          if (avail.start_time_3 && avail.end_time_3) {
            windows.push({
              id: `${avail.day_of_week}-${avail.start_time_3}-${avail.end_time_3}`,
              day: avail.day_of_week,
              availStart: avail.start_time_3,
              availEnd: avail.end_time_3,
              segments: []
            });
          }
        }
      });

      console.log('Availability windows created:', windows);

      // Fetch existing pricing rules
      const pricingResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/pricing`);
      console.log('Pricing rules fetched:', pricingResponse.data);
      
      // Map pricing rules to availability windows
      const updatedWindows = windows.map(window => {
        // Find pricing rules that fall within this specific window
        const matchingPrices = pricingResponse.data.filter(price => {
          // Must match day
          if (price.day_of_week !== window.day) return false;
          
          // Must fall within this window's time range
          // Use string comparison since times are in HH:MM format
          return price.start_time >= window.availStart && 
                 price.end_time <= window.availEnd;
        });

        console.log(`Window ${window.day} ${window.availStart}-${window.availEnd}: Found ${matchingPrices.length} pricing rules`);

        if (matchingPrices.length > 0) {
          // Sort by start time
          matchingPrices.sort((a, b) => a.start_time.localeCompare(b.start_time));
          
          window.segments = matchingPrices.map(price => ({
            start_time: price.start_time,
            end_time: price.end_time,
            price_per_mile: price.price_per_mile.toString()
          }));
        } else {
          // No pricing set - default to single segment covering FULL window
          console.log(`No pricing for ${window.day} ${window.availStart}-${window.availEnd}, creating default segment`);
          window.segments = [{
            start_time: window.availStart,  // Use availability start
            end_time: window.availEnd,      // Use availability end
            price_per_mile: ''
          }];
        }

        return window;
      });

      console.log('Updated windows with pricing:', updatedWindows);
      setAvailabilityWindows(updatedWindows);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
      alert('Failed to load pricing information');
    }
  };

  const handlePriceChange = (windowId, segmentIndex, value) => {
    setAvailabilityWindows(prev => prev.map(window => {
      if (window.id === windowId) {
        const newSegments = [...window.segments];
        newSegments[segmentIndex] = {
          ...newSegments[segmentIndex],
          price_per_mile: value
        };
        return { ...window, segments: newSegments };
      }
      return window;
    }));
  };

  const handleSegmentTimeChange = (windowId, segmentIndex, field, value) => {
    setAvailabilityWindows(prev => prev.map(window => {
      if (window.id === windowId) {
        const newSegments = [...window.segments];
        newSegments[segmentIndex] = {
          ...newSegments[segmentIndex],
          [field]: value
        };
        return { ...window, segments: newSegments };
      }
      return window;
    }));
  };

  const splitPricingWindow = (windowId) => {
    setAvailabilityWindows(prev => prev.map(window => {
      if (window.id === windowId) {
        const lastSegment = window.segments[window.segments.length - 1];
        const startTime = lastSegment.start_time;
        const endTime = lastSegment.end_time;
        
        // Helper to convert time to minutes
        const timeToMinutes = (time) => {
          const [h, m] = time.split(':').map(Number);
          return h * 60 + m;
        };
        
        // Helper to convert minutes to time string
        const minutesToTime = (minutes) => {
          const h = Math.floor(minutes / 60);
          const m = minutes % 60;
          return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        };
        
        const newSegments = [...window.segments];
        
        // Check if the last segment's end time matches the window's end time
        // This means the user hasn't manually adjusted it yet
        if (endTime === window.availEnd) {
          // Default behavior: split evenly in half
          const startMinutes = timeToMinutes(startTime);
          const endMinutes = timeToMinutes(endTime);
          const midMinutes = Math.floor((startMinutes + endMinutes) / 2);
          const midTime = minutesToTime(midMinutes);
          
          // Update last segment to end at midpoint
          newSegments[newSegments.length - 1] = {
            ...lastSegment,
            end_time: midTime
          };
          
          // Add new segment from midpoint to window end (continuous, no gap)
          newSegments.push({
            start_time: midTime,  // Starts exactly where previous ended
            end_time: window.availEnd,  // Always end at window end
            price_per_mile: ''
          });
          
          console.log(`Split ${window.day} evenly: ${startTime}-${midTime} and ${midTime}-${window.availEnd}`);
        } else {
          // User has manually set the end time - preserve it
          // New segment starts exactly where the previous ends (continuous pricing)
          const newStartTime = endTime;
          
          // New segment ends at window end
          newSegments.push({
            start_time: newStartTime,  // Starts exactly where previous ended
            end_time: window.availEnd,  // Always end at window end
            price_per_mile: ''
          });
          
          console.log(`Split ${window.day} preserving manual time: Kept ${startTime}-${endTime}, added ${newStartTime}-${window.availEnd}`);
        }

        return { ...window, segments: newSegments };
      }
      return window;
    }));
  };

  const removeSegment = (windowId, segmentIndex) => {
    setAvailabilityWindows(prev => prev.map(window => {
      if (window.id === windowId && window.segments.length > 1) {
        const newSegments = window.segments.filter((_, idx) => idx !== segmentIndex);
        
        // Adjust adjacent segments to fill the gap
        if (segmentIndex === 0) {
          // Removed first segment - extend next segment to availability start
          newSegments[0].start_time = window.availStart;
        } else if (segmentIndex === window.segments.length - 1) {
          // Removed last segment - extend previous segment to availability end
          newSegments[newSegments.length - 1].end_time = window.availEnd;
        } else {
          // Removed middle segment - extend previous segment to next segment's start
          newSegments[segmentIndex - 1].end_time = newSegments[segmentIndex].start_time;
        }
        
        return { ...window, segments: newSegments };
      }
      return window;
    }));
  };

  const validateAndSave = async () => {
    try {
      // Validate all windows
      for (const window of availabilityWindows) {
        // Check all segments have prices
        for (const segment of window.segments) {
          if (!segment.price_per_mile || parseFloat(segment.price_per_mile) <= 0) {
            alert(`Please set a valid price for all time segments in ${window.day}`);
            return;
          }
        }

        // Check segments cover full availability window
        const sortedSegments = [...window.segments].sort((a, b) => 
          a.start_time.localeCompare(b.start_time)
        );

        // First segment must start at availability start
        if (sortedSegments[0].start_time !== window.availStart) {
          alert(`${window.day} pricing must start at ${formatTime(window.availStart)}`);
          return;
        }

        // Last segment must end at availability end
        if (sortedSegments[sortedSegments.length - 1].end_time !== window.availEnd) {
          alert(`${window.day} pricing must end at ${formatTime(window.availEnd)}`);
          return;
        }

        // Check for gaps or overlaps
        for (let i = 0; i < sortedSegments.length - 1; i++) {
          if (sortedSegments[i].end_time !== sortedSegments[i + 1].start_time) {
            alert(`${window.day} has gaps or overlaps in pricing segments. Please ensure segments are continuous.`);
            return;
          }
        }
      }

      // All validation passed - save pricing
      setIsSaving(true);

      // Transform windows back to flat pricing rules
      const pricingRules = [];
      availabilityWindows.forEach(window => {
        window.segments.forEach(segment => {
          pricingRules.push({
            day_of_week: window.day,
            start_time: segment.start_time,
            end_time: segment.end_time,
            price_per_mile: parseFloat(segment.price_per_mile)
          });
        });
      });

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/pricing`,
        pricingRules,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      alert('Pricing saved successfully!');
      setIsSaving(false);
      // If in onboarding flow, redirect to onboarding; otherwise go to dashboard
      navigate(isOnboarding ? '/driver/onboarding' : '/driver/dashboard');
    } catch (error) {
      console.error('Error saving pricing:', error);
      alert('Failed to save pricing. Please try again.');
      setIsSaving(false);
    }
  };

  const formatTime = (time) => {
    const [h, minute] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${String(minute).padStart(2, '0')} ${ampm}`;
  };

  // NEW: Helper function to check if a day is required
  const isDayRequired = (dayName) => {
    return requiredDays.includes(dayName);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading pricing information...</div>
      </div>
    );
  }

  if (availabilityWindows.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(isOnboarding ? '/driver/onboarding' : -1)}
                className="text-gray-600 hover:text-gray-900"
              >
                ← {isOnboarding ? 'Back to Onboarding' : 'Back'}
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Set Pricing</h1>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-yellow-800 mb-2">No Availability Set</h2>
            <p className="text-yellow-700 mb-4">
              You need to set your availability before you can configure pricing. 
              Pricing windows are based on your available time slots.
            </p>
            <button
              onClick={() => navigate('/driver/availability')}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              Set Availability
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Set Pricing</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* NEW: Required Days Warning Banner */}
        {requiredDays.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Pricing Update Required
                </h3>
                <div className="mt-1 text-sm text-red-700">
                  <p>
                    You updated availability for: <strong>{requiredDays.join(', ')}</strong>
                  </p>
                  <p className="mt-1">
                    Days marked with <span className="text-red-500 font-bold">*</span> require updated pricing before riders can book.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">How Pricing Works</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Pricing windows are automatically created from your availability</li>
            <li>• Each availability window starts with a single price for the entire time range</li>
            <li>• Use "Split Pricing" to create different prices for different times within the same window</li>
            <li>• When you split, the new segment automatically starts where the previous one ended</li>
            <li>• You can adjust split times manually, but they must cover your full availability with no gaps</li>
            <li>• If you have multiple time windows per day (e.g., 6-11am, 1-5pm, 9-11pm), each gets its own pricing section</li>
          </ul>
        </div>

        <div className="space-y-6">
          {availabilityWindows.map((window, windowIndex) => (
            <div key={window.id} className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                    {window.day}
                    {/* NEW: Show red asterisk if day is required */}
                    {isDayRequired(window.day) && (
                      <span className="text-red-500 ml-1 text-2xl" title="Pricing required - availability was updated">*</span>
                    )}
                    {/* Show window number if multiple windows for same day */}
                    {availabilityWindows.filter(w => w.day === window.day).length > 1 && (
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        (Window {availabilityWindows.filter((w, i) => w.day === window.day && i <= windowIndex).length})
                      </span>
                    )}
                  </h2>
                  <p className="text-sm text-gray-600">
                    Available: {formatTime(window.availStart)} - {formatTime(window.availEnd)}
                  </p>
                </div>
                <button
                  onClick={() => splitPricingWindow(window.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm self-start sm:self-auto"
                >
                  + Split Pricing
                </button>
              </div>

              <div className="space-y-3">
                {window.segments.map((segment, index) => (
                  <div key={index} className="p-3 sm:p-4 border rounded-lg bg-gray-50">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={segment.start_time}
                          onChange={(e) => handleSegmentTimeChange(window.id, index, 'start_time', e.target.value)}
                          className="w-full px-2 sm:px-3 py-2 border rounded text-sm"
                          min={window.availStart}
                          max={window.availEnd}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          End Time
                        </label>
                        <input
                          type="time"
                          value={segment.end_time}
                          onChange={(e) => handleSegmentTimeChange(window.id, index, 'end_time', e.target.value)}
                          className="w-full px-2 sm:px-3 py-2 border rounded text-sm"
                          min={window.availStart}
                          max={window.availEnd}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          $/Mile
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={segment.price_per_mile}
                          onChange={(e) => handlePriceChange(window.id, index, e.target.value)}
                          className="w-full px-2 sm:px-3 py-2 border rounded text-sm"
                          placeholder="2.50"
                        />
                      </div>

                      {window.segments.length > 1 && (
                        <div className="flex items-end">
                          <button
                            onClick={() => removeSegment(window.id, index)}
                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="mt-2 text-xs text-gray-600">
                      Segment {index + 1}: {formatTime(segment.start_time)} - {formatTime(segment.end_time)}
                      {segment.price_per_mile && ` • $${segment.price_per_mile}/mile`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={validateAndSave}
            disabled={isSaving}
            className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
          >
            {isSaving ? 'Saving...' : 'Save All Pricing'}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </main>
    </div>
  );
};

export default DriverPricing;